/**
 * PulseItemForm — dialog / inline form for creating (or editing) a Neighborhood
 * Pulse post. Image is compressed client-side before upload to keep payloads
 * small and stay within backend limits.
 *
 * Props:
 *   neighborhoodSlug  – optional. Attach the post to a specific /pulse/{slug}.
 *   city, state, country, lat, lng  – optional context to pass through.
 *   onSaved(item)     – called after successful create/edit.
 *   onCancel()        – called when the user aborts.
 *   editing           – if set, pre-fill and PATCH instead of POST.
 */
import { useState } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { Loader2, Save, X, Image as ImageIcon, Sparkles, ExternalLink } from "lucide-react";
import AIImproveButton from "@/components/AIImproveButton";
import SmartTextInput from "@/components/SmartTextInput";

async function compressImage(file, { maxDim = 1200, quality = 0.75 } = {}) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = rej;
      i.src = url;
    });
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(img, 0, 0, w, h);
    return canvas.toDataURL("image/jpeg", quality);
  } finally { URL.revokeObjectURL(url); }
}

export default function PulseItemForm({ neighborhoodSlug, city, state, country, lat, lng, onSaved, onCancel, editing }) {
  const [form, setForm] = useState({
    title: editing?.title || "",
    description: editing?.description || "",
    image_url: editing?.image_url || "",
    tags: (editing?.tags || []).join(", "),
    notes: editing?.notes || "",
    website_url: editing?.website_url || "",
    youtube_url: editing?.youtube_url || "",
    instagram_url: editing?.instagram_url || "",
    category: editing?.category || "community",
  });
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please pick an image."); return; }
    try {
      const data_url = await compressImage(f);
      set("image_url", data_url);
    } catch { toast.error("Couldn't process that image. Try another one."); }
  };

  const summarize = async () => {
    if (!form.notes && !form.title) { toast.error("Add some notes first."); return; }
    setAiBusy(true);
    try {
      const { data } = await http.post("/pulse-items/ai-summary", { text: form.notes || form.title, title: form.title });
      if (data.description) {
        set("description", data.description);
        toast.success("Description generated.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI unavailable — please write a description manually.");
    } finally { setAiBusy(false); }
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required.");
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        image_url: form.image_url || null,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        notes: form.notes.trim() || null,
        website_url: form.website_url.trim() || null,
        youtube_url: form.youtube_url.trim() || null,
        instagram_url: form.instagram_url.trim() || null,
        neighborhood_slug: neighborhoodSlug || null,
        city: city || null, state: state || null, country: country || null,
        lat: lat || null, lng: lng || null,
        category: form.category || "community",
      };
      let item;
      if (editing?.id) {
        const { data } = await http.patch(`/pulse-items/${editing.id}`, payload);
        item = data;
      } else {
        const { data } = await http.post("/pulse-items", payload);
        item = data;
      }
      toast.success(editing ? "Post updated" : "Posted to Neighborhood Pulse");
      onSaved?.(item);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="wofy-card !p-6 space-y-4" data-testid="pulse-item-form">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl">{editing ? "Edit your post" : "Add a Neighborhood Pulse post"}</h3>
        {onCancel && (
          <button onClick={onCancel} className="opacity-60 hover:opacity-100" aria-label="Close" data-testid="pulse-form-close">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <Field label="Title *">
        <input className="wofy-input" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="What's happening in your neighborhood?" data-testid="pulse-form-title" maxLength={200} />
      </Field>

      <Field label="Category" hint="Drives homepage rotation: one Featured item per category.">
        <div className="flex flex-wrap gap-2" data-testid="pulse-form-category-group">
          {[
            { v: "community", label: "Community", emoji: "🤝" },
            { v: "event",     label: "Event",     emoji: "📅" },
            { v: "news",      label: "News",      emoji: "📰" },
            { v: "info",      label: "Info",      emoji: "ℹ️" },
          ].map((c) => (
            <button
              key={c.v}
              type="button"
              onClick={() => set("category", c.v)}
              className="text-sm inline-flex items-center gap-1 px-3 py-1.5 rounded-full border transition"
              style={form.category === c.v
                ? { borderColor: "var(--wofy-primary)", background: "var(--wofy-primary)", color: "#fff" }
                : { borderColor: "var(--wofy-line)", color: "var(--wofy-text)" }}
              data-testid={`pulse-form-category-${c.v}`}
            >
              <span>{c.emoji}</span> {c.label}
            </button>
          ))}
        </div>
      </Field>

      <Field label="Short description" hint="~2–3 sentences. Use ✨ AI to auto-summarise from your notes below.">
        <SmartTextInput
          value={form.description}
          onChange={(v) => set("description", v)}
          rows={3}
          maxLength={800}
          testid="pulse-form-desc"
        />
        <div className="mt-1 flex items-center gap-2">
          <button type="button" onClick={summarize} disabled={aiBusy} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border" style={{ borderColor: "var(--wofy-primary)", color: "var(--wofy-primary)" }} data-testid="pulse-form-summarize">
            {aiBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            {aiBusy ? "Summarising…" : "✨ Auto-summarise from notes"}
          </button>
        </div>
      </Field>

      <Field label="Image (optional)" hint="Auto-compressed to fit — no need to resize first.">
        {form.image_url ? (
          <div className="relative inline-block">
            <img src={form.image_url} alt="Preview" className="max-h-48 rounded-lg" />
            <button onClick={() => set("image_url", "")} className="absolute -top-2 -right-2 bg-black/70 text-white w-6 h-6 rounded-full inline-flex items-center justify-center" data-testid="pulse-form-remove-image">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <label className="wofy-btn-ghost text-sm cursor-pointer inline-flex">
            <ImageIcon className="w-4 h-4" /> Add photo
            <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="pulse-form-file" />
          </label>
        )}
      </Field>

      <Field label="Tags" hint="Comma-separated (e.g. potholes, garden, festival).">
        <input className="wofy-input" value={form.tags} onChange={(e) => set("tags", e.target.value)} placeholder="community, garden, event" data-testid="pulse-form-tags" />
      </Field>

      <Field label="Notes / comments" hint="Longer background. Optional.">
        <SmartTextInput
          value={form.notes}
          onChange={(v) => set("notes", v)}
          rows={4}
          maxLength={5000}
          testid="pulse-form-notes"
        />
      </Field>

      <div className="grid sm:grid-cols-3 gap-3">
        <Field label="Website">
          <input type="url" className="wofy-input" value={form.website_url} onChange={(e) => set("website_url", e.target.value)} placeholder="https://…" data-testid="pulse-form-website" />
        </Field>
        <Field label="YouTube">
          <input type="url" className="wofy-input" value={form.youtube_url} onChange={(e) => set("youtube_url", e.target.value)} placeholder="https://youtube.com/…" data-testid="pulse-form-youtube" />
        </Field>
        <Field label="Instagram">
          <input type="url" className="wofy-input" value={form.instagram_url} onChange={(e) => set("instagram_url", e.target.value)} placeholder="https://instagram.com/…" data-testid="pulse-form-instagram" />
        </Field>
      </div>

      <div className="flex items-center gap-3 pt-2">
        <button onClick={save} disabled={busy} className="wofy-btn-primary" data-testid="pulse-form-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {editing ? "Save changes" : "Post to Neighborhood Pulse"}
        </button>
        {onCancel && (
          <button onClick={onCancel} className="wofy-btn-ghost text-sm">Cancel</button>
        )}
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-mono uppercase tracking-widest mb-1.5" style={{ color: "var(--wofy-text-soft)" }}>{label}</span>
      {children}
      {hint && <span className="block text-xs mt-1 opacity-70">{hint}</span>}
    </label>
  );
}
